import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Button, Dialog, FormLabel, Radio, Typography, useModalPortal } from '@screentone/core';

import { ResponsiveLoader } from 'components';
import { ArtifactType, Layout, useGetArtifactSettingsQuery } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';

interface SelectLayoutModalProps {
  onDismiss: () => void;
}

export const SelectLayoutModal = ({ onDismiss }: SelectLayoutModalProps) => {
  const { renderNode } = useModalPortal();
  const { currentProperty: routeProperty } = useAuth();
  const currentProperty = useConvertedProperty();
  const navigate = useNavigate();

  const { data, isLoading } = useGetArtifactSettingsQuery(
    { publicationKey: currentProperty!, artifactType: ArtifactType.Layout },
    { enabled: !!currentProperty }
  );

  const layouts = data?.getArtifactSettings ?? [];
  const [selectedLayoutId, setSelectedLayoutId] = useState('');

  const handleCreate = () => {
    if (!selectedLayoutId) return;
    navigate(`/${routeProperty}/pages/new?layoutId=${selectedLayoutId}`);
  };

  return (
    <Dialog
      status="open"
      onDismiss={onDismiss}
      focusTrap
      renderNode={renderNode}
      style={{ display: 'flex', flexDirection: 'column', maxHeight: '80vh', overflow: 'hidden' }}
    >
      <Dialog.Title>Select a layout</Dialog.Title>
      <Dialog.Content style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isLoading ? (
          <ResponsiveLoader />
        ) : layouts.length === 0 ? (
          <Typography variant="bodytext">No layouts available for this publication.</Typography>
        ) : (
          layouts.map((layout) => {
            const body = layout.body as Layout;
            return (
              <div key={layout.artifactId}>
                <FormLabel label={body.name} labelPosition="right">
                  <Radio
                    checked={selectedLayoutId === body.id}
                    id={layout.artifactId}
                    name="layout-select"
                    onChange={() => setSelectedLayoutId(body.id)}
                  />
                </FormLabel>
              </div>
            );
          })
        )}
      </Dialog.Content>
      <Dialog.Actions style={{ flexShrink: 0 }}>
        <Button secondary onClick={onDismiss}>
          Cancel
        </Button>
        <Button primary onClick={handleCreate} disabled={!selectedLayoutId || isLoading}>
          Create page
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
